import styles from './DashboardHeader.module.scss';
import { ACCESS_TYPES, DATA_POSITION, MAGIC_UPLOAD_TYPE } from '../../../../store/investor/constants';
import { FEATURE_STATUS } from '../../../../store/user/constants';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { backButtonImg, backButtonImgHovered } from './TabConstants';
import ReportNavigation from './components/ReportNavigation/ReportNavigation';
import HeaderButtonContainer from './components/HeaderButtonContainer/HeaderButtonContainer';
import { useEffect, useState } from 'react';
import { INVESTOR_ROUTES } from '../../../../utils/constants/routesConst';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import {
    caretLeftFill,
    commentsIcon,
    EDIT_PROFILE_ICON,
    leftPanelCloseSvg,
    magicUploadIcon,
    magicUploadIconColor,
} from '../../../../assets/hostedassets';
import { closeCommentsThread, setShowCommentsThread } from '../../../../store/commentsThread/commentsThread_actions';
import clsx from 'clsx';
import DownloadForOfflineIcon from '../../../../assets/Svg/DownloadForOfflineIcon';
import { removeTooltipContent, setTooltipContent } from '../../../../store/tooltip/tooltip_actions';
import { USER_TYPE } from '../../../../utils/constants/user';
import { Mixpanel } from '../../../../utils/mixpanel';
import { getSubdomain } from '../../../../utils/utils';
import { AICA_MAGIC_UPLOAD, AICA_VIEW_COMMENT } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { setDealDrawer } from '../../../../store/investor/action';
import { pickWebsite } from '../../../../utils/urlMetadata';
import CompanyLogoBadge from '../../../../common/_custom/CompanyLogoBadge';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { TOPBAR_CONSTANTS } from '../../../../common/Topbar/Topbar';
import AIText from '../../../../common/AIText/AIText';

type DashboardHeaderProps = {
    isUserRestricted: boolean;
    selectedCompany: any;
    metricScoreGenerated?: boolean;
    refreshReportStatus: string;
    downloadPDFStatus: string;
    goBackToPortfolio: () => void;
    renderHeaderButton: () => void;
    exportData: () => void;
    // Mobile: reuse this header but hide the report tabs + right-side actions
    // (export / magic-upload / edit-profile), keeping back + logo + name + comment.
    isMobile?: boolean;
    // Mobile-only: controls the panel-toggle icon + lets the parent (Dashboard) own
    // whether the mobile TabSelector overlay is open — RECUR-27903.
    mobileMenuOpen?: boolean;
    onToggleMobileMenu?: () => void;
};

const DashboardHeader = (props: DashboardHeaderProps) => {
    const {
        isUserRestricted,
        selectedCompany,
        metricScoreGenerated,
        refreshReportStatus,
        downloadPDFStatus,
        goBackToPortfolio,
        exportData,
        renderHeaderButton,
        isMobile = false,
        mobileMenuOpen = false,
        onToggleMobileMenu,
    } = props;
    const dispatch = useDispatch();
    const { magicUploadVisibility } = useFeatureVisibility({
        magicUploadVisibility: VISIBILITY_KEYS.MAGIC_UPLOAD.ENABLED,
    });
    const isDSInvestor = localStorage?.getItem?.('isDSInvestor') === 'true';
    const permissions = useAppSelector((state) => state.investor.permissions);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const userData = useAppSelector((state) => state.user.userData);
    const untaggedCount = useAppSelector((state) => state.investor.untaggedCount);
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    // The company's real website (for the logo) lives in the Overview page's
    // competitor data; Overview shares it via Redux. Prefer that, then the base
    // company website — pickWebsite skips sentinels like "NA".
    const companyLogoWebsite = useAppSelector((state) => state.investor.companyLogoWebsite);
    const [selected, setSelected] = useState<any>({
        id: 'report',
        label: 'Aica Report',
        img: 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/AicaReportHeaderIcon.svg',
        route: '',
        isLocked: false,
    });
    const isMagicUploadOpen =
        searchParams.get('showNeedInfo') === 'true' &&
        searchParams.get('isCompany') === 'true' &&
        searchParams.get('magicUpload') === 'true';

    const isPartnerOrLender = [USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM, USER_TYPE.PARTNER].includes(userData.accountType);
    useEffect(() => {
        if (selected.id === 'dataroom') {
            navigate(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DATA_ROOM_DETAILS}${params.id ?? ''}?${searchParams.toString()}`);
        }
    }, [selected]);

    const openCloseCommentsThread = () => {
        if (commentsThreadState.showCommentsThread) {
            dispatch(closeCommentsThread());
        } else {
            dispatch(
                setShowCommentsThread({
                    source: 'DASHBOARD',
                    assesseeOrgId: params.id as string,
                }),
            );

            Mixpanel.track(AICA_VIEW_COMMENT, {
                userRole: userData?.accountType,
                pnoPlatform: getSubdomain(),
                pnoId: userData?.investorOrgId,
                userId: userData?.userId,
                currentUrl: window.location.href,
                accessibility: DATA_POSITION.REPORT,
                organizationId: params.id,
            });
        }
    };

    const openMagicUpload = () => {
        searchParams.set('showNeedInfo', 'true');
        searchParams.set('isCompany', 'true');
        searchParams.set('magicUpload', 'true');
        setSearchParams(searchParams);

        Mixpanel.track(AICA_MAGIC_UPLOAD, {
            userRole: userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: userData?.investorOrgId,
            userId: userData?.userId,
            currentUrl: window.location.href,
            accessedPlatform: userData?.accountType,
            individualGlobal: MAGIC_UPLOAD_TYPE.INDIVIDUAL,
            organizationId: params.id,
        });
    };

    // RECUR-21135: Brand name (displayName) as the primary company name in this mini header, with a
    // falsy-safe fallback to the legal orgName (then legacy organisationName) so an empty-string
    // brand or orgName doesn't blank the header. Matches the name already shown on Deals and
    // elsewhere (getDisplayName). Supersedes the earlier "legal name in header" decision (#3308).
    const headerCompanyName = selectedCompany?.displayName || selectedCompany?.orgName || selectedCompany?.organisationName || '';

    const showTooltipForName = (e) => {
        const target = e.currentTarget;
        if (target.scrollWidth <= target.clientWidth) return;
        dispatch(
            setTooltipContent({
                content: headerCompanyName,
                target: e.currentTarget,
                placement: 'bottom',
            }),
        );
    };

    const hideTooltip = () => {
        removeTooltipContent(dispatch);
    };

    const handleGoBack = () => {
        goBackToPortfolio();
    };

    /*
        Mobile swaps the 44x40 white-pill graphic for CaretLeftFill, which already points back, so
        nothing here rotates it. The hover pair only exists for the desktop asset, and a tap on a
        phone can still fire mouseenter — which would swap the icon out for
        dashBoardHeaderBackButtonHovered mid-press — so the handlers come off entirely on mobile.
        RECUR-27903.
    */
    const backIcon = (
        <img
            onMouseEnter={isMobile ? undefined : (e) => (e.currentTarget.src = backButtonImgHovered)}
            onMouseLeave={isMobile ? undefined : (e) => (e.currentTarget.src = backButtonImg)}
            alt="back"
            src={isMobile ? caretLeftFill : backButtonImg}
            className={clsx(styles.HeaderBackButton, { [styles.HeaderBackButtonMobile]: isMobile })}
            onClick={isMobile ? undefined : handleGoBack}
        />
    );

    // Mobile-only: a bare <img onClick> isn't focusable, so keyboard users can't go back. Desktop
    // keeps rendering backIcon directly — same element, same handlers — untouched. RECUR-27903.
    const backButton = !isUserRestricted ? (
        isMobile ? (
            <button type="button" className={styles.HeaderBackButtonMobileControl} onClick={handleGoBack} aria-label="Go back">
                {backIcon}
            </button>
        ) : (
            backIcon
        )
    ) : null;

    return (
        <div className={clsx(styles.DashboardHeader, { [styles.Mobile]: isMobile })}>
            <div className={styles.CompanyName}>
                {/* Desktop keeps the back button as a direct flex child of .CompanyName. Wrapping it
                    unconditionally added an extra flex item there — which for restricted users (no back
                    button) was an empty div still consuming .CompanyName's 10px gap, shifting the logo. */}
                {isMobile ? (
                    <div className={styles.MobileNavActions}>
                        {backButton}
                        <button
                            type="button"
                            className={styles.MobileMenuButton}
                            onClick={onToggleMobileMenu}
                            aria-expanded={mobileMenuOpen}
                            aria-label={mobileMenuOpen ? 'Collapse menu' : 'Expand menu'}
                            title={mobileMenuOpen ? 'Collapse menu' : 'Expand menu'}
                        >
                            <img
                                src={leftPanelCloseSvg}
                                alt=""
                                width="24"
                                height="24"
                                className={clsx(styles.MobileMenuIcon, { [styles.MobileMenuIconFlipped]: !mobileMenuOpen })}
                            />
                        </button>
                    </div>
                ) : (
                    backButton
                )}
                <div className={styles.Name}>
                    <CompanyLogoBadge
                        name={headerCompanyName}
                        logo={selectedCompany?.logo}
                        websiteUrl={pickWebsite(companyLogoWebsite, selectedCompany?.websiteUrl)}
                        size={36}
                        shape="square"
                    />
                    <div className={styles.NameTextContainer}>
                        <div
                            className={styles.NameText}
                            title={headerCompanyName}
                            onMouseOver={showTooltipForName}
                            onMouseLeave={hideTooltip}
                        >
                            {headerCompanyName}
                        </div>
                        <div className={styles.SubText}>Created on {dayjs(selectedCompany?.dateCreated).format('DD MMM YYYY')}</div>
                    </div>
                </div>
                {/* {selectedCompany?.companyAccessType !== ACCESS_TIERS.WATCH &&
                   metricScoreGenerated && (
                       <AicaGradeBatch
                           showAicaGradeInfo={() => dispatch(setShowAicaGradeDrawer(true))}
                       />
                   )} */}
            </div>
            <div className={styles.DashboardHeaderMiddle}>
                {!isMobile && !isDSInvestor && <ReportNavigation selectedCompany={selectedCompany} />}
                {/*<TabSlider*/}
                {/*    selected={selected}*/}
                {/*    onChange={(tab) => {*/}
                {/*        setSelected(tab);*/}
                {/*    }}*/}
                {/*    navbarItems={[...tabOptions]}*/}
                {/*/>*/}
            </div>
            <div className={styles.DashboardHeaderRight}>
                <>
                    {!isMobile && permissions?.TRACK !== ACCESS_TYPES.LOCKED && renderHeaderButton()}
                    {!isMobile &&
                        magicUploadVisibility.show &&
                        (!isPartnerOrLender ? (
                            <div
                                className={clsx(styles.MessageIcon, {
                                    [styles.MessageIcon_Active]: isMagicUploadOpen,
                                })}
                                onClick={openMagicUpload}
                            >
                                <img src={magicUploadIcon} alt="comments" height={'24px'} />
                            </div>
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <div className={styles.MagicUploadButton} onClick={openMagicUpload}>
                                    <DownloadForOfflineIcon fill="#6021B3" width={16} height={16} />
                                    <AIText
                                        text={
                                            userData.accountType === USER_TYPE.PARTNER
                                                ? TOPBAR_CONSTANTS.BUTTONS.UPLOAD_DATA
                                                : TOPBAR_CONSTANTS.BUTTONS.MAGIC_UPLOAD
                                        }
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </div>
                            </div>
                        ))}
                    {!isPartnerOrLender && (
                        <>
                            {!isMobile && (
                                <div
                                    className={clsx(styles.MessageIcon, {})}
                                    onClick={() => {
                                        dispatch(
                                            setDealDrawer({
                                                drawerSource: 'Debt Products',
                                                open: true,
                                                drawerLabel: 'Edit Profile',
                                                companyName: selectedCompany?.orgName,
                                            }),
                                        );
                                        dispatch(closeCommentsThread());
                                    }}
                                >
                                    <img src={EDIT_PROFILE_ICON} alt="comments" height={'24px'} />
                                </div>
                            )}
                            <div
                                className={clsx(styles.MessageIcon, {
                                    [styles.MessageIcon_Active]:
                                        commentsThreadState.showCommentsThread && !commentsThreadState.closingCommentsThread,
                                })}
                                onClick={openCloseCommentsThread}
                            >
                                <img src={commentsIcon} alt="comments" height={'24px'} />
                            </div>
                        </>
                    )}

                    {!isMobile && (
                        <HeaderButtonContainer
                            exportData={exportData}
                            selectedCompany={selectedCompany}
                            refreshReportStatus={refreshReportStatus}
                            downloadPDFStatus={downloadPDFStatus}
                        />
                    )}
                    {!isMobile && refreshReportStatus && refreshReportStatus === FEATURE_STATUS.SHOW && (
                        <div className={styles.RefreshReport}>Refresh Report</div>
                    )}
                    {!isMobile && downloadPDFStatus && downloadPDFStatus === FEATURE_STATUS.SHOW && (
                        <div className={styles.DownloadPDF}>Download PDF</div>
                    )}
                </>
            </div>
        </div>
    );
};

export default DashboardHeader;
